import type { BulkActionRequest, Ipv6AddressMode, Ipv6PrefixMode } from '@simpleacs/shared';

/**
 * The bulk "WAN mode" form's own rules — pure, so the page's Build button and
 * its reset-on-Bridging are pinned without a DOM.
 *
 * The form mirrors the WAN dialog's steps (VLAN → Mode → Addressing → IP
 * protocol → Firewall) for a RUN rather than one ONU, so where the dialog
 * opens on one ONU's current value the form offers a word meaning "each ONU's
 * own" (`vlanId: null`, `addressing: 'recorded'`, `ipv6: 'current'`,
 * `firewall: 'current'`), and every other value is the step's own opening
 * value. It types no password and no address: those are per subscriber, and
 * the resolver reads each ONU's row.
 */
export type BulkWanModeRequest = Extract<BulkActionRequest, { action: 'wan_mode' }>;

/**
 * Where the form opens. Null VLAN = each ONU's own — there is no "unchosen"
 * here because "as recorded" is a real choice, unlike `attach_vlan`'s 0;
 * routed because the TR-069-default fleet's WANs are routed; every other value
 * is its step's opening value.
 */
export function defaultWanModeRequest(): BulkWanModeRequest {
  return {
    action: 'wan_mode',
    vlanId: null,
    mode: 'route',
    addressing: 'recorded',
    staticDns: null,
    ipv6: 'current',
    firewall: 'current',
  };
}

/**
 * Choosing Bridging takes the Addressing and IP protocol steps off the row —
 * a bridged VLAN is handed to the subscriber's own router — so the values
 * those steps held go back to their opening words, or the request would
 * carry an addressing the schema refuses beside `bridge`.
 */
export function withWanMode(
  request: BulkWanModeRequest,
  mode: 'route' | 'bridge',
): BulkWanModeRequest {
  if (mode === 'bridge')
    return { ...request, mode, addressing: 'recorded', ipv6: 'current', staticDns: null };
  return { ...request, mode };
}

/**
 * Changing the chassis throws away what belonged to the old one — for this
 * form that is the VLAN (the page's `clearVlans`) AND the two steps the
 * dialog draws differently per vendor. On a Huawei chassis PPPoE and Static
 * are disabled tiles (the CPE takes those over TR-069) and there is no
 * Firewall step at all (`wanWizardSteps` drops it), so a `pppoe`/`static`
 * or an `on`/`off` chosen on a ZTE chassis and carried across is a request
 * the form can no longer show: React keeps a disabled option selected, the
 * Firewall block vanishes while its value still rides the request, and the
 * server refuses every row that does not record the addressing or warns on
 * every row that the firewall is not written (review, 2026-09-14). The
 * DNS goes with the addressing — on a Huawei chassis no IP profile is
 * written from it. DHCP, IPv6 and Bridged are the same on either chassis
 * and are kept; a ZTE chassis, or one not yet known, changes nothing.
 */
export function withOltVendor(
  request: BulkWanModeRequest,
  vendor: string | null | undefined,
): BulkWanModeRequest {
  if (vendor !== 'huawei') return request;
  const addressing =
    request.addressing === 'pppoe' || request.addressing === 'static'
      ? 'recorded'
      : request.addressing;
  return { ...request, addressing, staticDns: null, firewall: 'current' };
}

/** Choosing an addressing that carries no DNS drops the DNS box's value with it. */
export function withWanAddressing(
  request: BulkWanModeRequest,
  addressing: BulkWanModeRequest['addressing'],
): BulkWanModeRequest {
  const keepsDns = addressing === 'static' || addressing === 'recorded';
  return { ...request, addressing, staticDns: keepsDns ? request.staticDns : null };
}

/** The dual-stack pair the form edits, or the platform's own opening pair when the run says "current" or IPv4. */
export function ipv6ModesOf(request: BulkWanModeRequest): {
  addressMode: Ipv6AddressMode;
  prefixMode: Ipv6PrefixMode;
} {
  if (request.ipv6 !== 'current' && request.ipv6.protocol === 'dual_stack') {
    return { addressMode: request.ipv6.addressMode, prefixMode: request.ipv6.prefixMode };
  }
  // Auto + DHCPv6-PD: the firmware default every family ships as (IPV6_*_DEFAULT_ORDER).
  return { addressMode: 'auto', prefixMode: 'dhcpv6_pd' };
}

const IPV4 = /^(25[0-5]|2[0-4]\d|1?\d?\d)(\.(25[0-5]|2[0-4]\d|1?\d?\d)){3}$/;

/**
 * Whether the request can be planned, and the one sentence that says why not.
 * The server refuses each of these at the boundary (`createBulkPlanSchema`);
 * this only exists so the operator is not sent to build a plan that comes
 * back a 400.
 */
export function wanModeReady(request: BulkWanModeRequest): {
  ready: boolean;
  reason: string | null;
} {
  if (request.ipv6 !== 'current' && request.ipv6.protocol === 'dual_stack') {
    if (request.ipv6.addressMode === 'none' && request.ipv6.prefixMode === 'none') {
      return {
        ready: false,
        reason:
          'Dual stack with neither an IPv6 address mode nor a prefix mode is IPv4 only — choose one, or choose IPv4 only.',
      };
    }
  }
  if (
    request.staticDns !== null &&
    request.staticDns.trim() !== '' &&
    !IPV4.test(request.staticDns.trim())
  ) {
    return {
      ready: false,
      reason: `"${request.staticDns}" is not a valid primary DNS address (an IPv4 address such as 8.8.8.8).`,
    };
  }
  return { ready: true, reason: null };
}

/** The request as it is sent: an empty DNS box is null, not "". */
export function wanModeRequestToSend(request: BulkWanModeRequest): BulkWanModeRequest {
  const dns = request.staticDns?.trim() ?? '';
  return { ...request, staticDns: dns === '' ? null : dns };
}
